import { FiCheck, FiSearch } from "react-icons/fi";
import { forwardRef } from "react";
import RequiredLabel from "../components/DynamicFieldRenderer/shared/RequiredLabel";

export const Card = ({
  children,
  className = "",
  border = true,
  rounded = true,
  variant = "default",
  ...props
}) => {
  const variants = {
    default: "bg-white",
    primary: "bg-primary text-white",
    secondary: "bg-secondary text-white",
    success: "bg-success text-white",
    warning: "bg-warning text-white",
    danger: "bg-danger text-white",
    light: "bg-customGrayExtraLight text-black",
    outlinePrimary: "border border-primary text-primary bg-white",
    outlineSuccess: "border border-success text-success bg-white",
    outlineDanger: "border border-danger text-danger bg-white",
  };

  const baseClasses = "overflow-hidden";
  const borderClasses = border
    ? "border border-customGrayLight"
    : "border-0 shadow-none";
  const roundedClasses = rounded ? "rounded-lg" : "rounded-none";

  const classes = [
    baseClasses,
    variants[variant] || variants.default,
    borderClasses,
    className,
    roundedClasses,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export const Button = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  loading = false,
  rounded = false,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const roundedClasses = rounded ? "rounded-full" : "rounded-lg";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-hover ",
    secondary: "bg-customGrayLight text-gray-700 hover:bg-customGray",
    danger: "bg-danger text-white hover:bg-danger-hover",
    success: "bg-success text-white hover:bg-success-hover",
    link: "bg-transparent text-primary hover:text-primary-hover underline",
    info: "bg-info text-white hover:bg-info-hover",
    warning: "bg-warning text-white hover:bg-warning-hover",
    outlineSecondary: "ring-2 ring-gray-300 text-gray-700 hover:bg-gray-100",
    outlinePrimary: "ring-2 ring-primary text-primary hover:bg-primary/10",
    outlineSuccess: "ring-2 ring-success text-success hover:bg-success/10",
    outlineDanger: "ring-2 ring-danger text-danger hover:bg-danger/10",
    outlineInfo: "ring-2 ring-info text-info hover:bg-info/10",
    outlineWarning: "ring-2 ring-warning text-warning hover:bg-warning/10",
  };

  const sizes = {
    xs: "px-2 py-1 md:px-2.5 md:py-1 text-xs md:text-xs",
    sm: "px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm",
    md: "px-3 py-2 md:px-4 md:py-2 text-sm",
    lg: "px-4 py-2 md:px-6 md:py-3 text-sm md:text-base",
  };

  return (
    <button
      disabled={loading}
      className={`${baseClasses} ${roundedClasses} ${variants[variant]} ${
        sizes[size]
      } ${className} ${loading ? "animate-pulse" : ""}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input = forwardRef(
  ({ dir = "auto", className = "", ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={`w-full px-3 py-2 border border-customGray rounded-lg focus:border-primary md:text-sm text-base ${className}`}
        {...props}
        dir={dir}
      />
    );
  }
);

export const Label = ({ required, htmlFor = "", label = "" }) => {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-sm font-bold text-gray-700 mb-2"
    >
      {label}
      <RequiredLabel isRequired={required} />
    </label>
  );
};

export const Textarea = forwardRef(
  ({ dir = "auto", className = "", ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={`w-full px-3 py-2 border border-customGrayLight rounded-lg focus:border-primary text-sm resize-vertical overflow-auto ${className}`}
        {...props}
        dir={dir}
      />
    );
  }
);

export const Select = ({ children, className = "", ...props }) => {
  return (
    <select
      name="select"
      className={`w-full px-3 py-2 border border-customGrayLight rounded-lg focus:border-primary text-sm bg-white ${className}`}
      {...props}
    >
      {children}
    </select>
  );
};

export const Switch = ({ onChange, isOn, disabled }) => {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 hover:opacity-75 ${
        isOn ? "bg-primary" : "bg-gray-200"
      }`}
      disabled={disabled}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 ease-in-out absolute ${
          isOn ? "start-1" : "end-1"
        } `}
      />
    </button>
  );
};

export const Checkbox = ({ onChange, checked, disabled }) => {
  return (
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
        disabled={disabled}
      />
      <div
        className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
          checked
            ? "bg-primary border-primary shadow-sm"
            : "bg-white border-gray-300 group-hover:border-primary/50"
        }`}
      >
        {checked && <FiCheck className="w-3 h-3 text-white" />}
      </div>
    </div>
  );
};

export const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-customGrayLight text-gray-700",
    primary: "bg-primary-light text-primary",
    success: "bg-success-light text-success",
    warning: "bg-warning-light text-warning",
    danger: "bg-danger/10 text-danger",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}
    >
      {children}
    </span>
  );
};

export const IconButton = ({
  icon: Icon,
  variant = "secondary",
  className = "",
  ...props
}) => {
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-hover",
    secondary: "bg-customGrayLight text-gray-700 hover:bg-customGray",
    danger: "bg-danger text-white hover:bg-danger-hover",
  };

  return (
    <button
      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${variants[variant]} ${className}`}
      {...props}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

export const SectionHeader = ({
  icon: Icon,
  title,
  subtitle,
  className = "",
  titleType,
  titleClass,
  iconSize = 5,
  iconColor = "text-white",
  iconBgColor = "bg-primary",
  titleCss = {},
  imageSrc
}) => {
  const Title = () => {
    const getitleClasses = titleClass || `text-xl font-bold text-gray-900`;
    switch (titleType) {
      case "h1":
        return <h1 className={getitleClasses} style={titleCss}>{title}</h1>;
      case "h2":
        return <h2 className={getitleClasses} style={titleCss}>{title}</h2>;
      case "h3":
        return <h3 className={getitleClasses} style={titleCss}>{title}</h3>;
      case "h4":
        return <h4 className={getitleClasses} style={titleCss}>{title}</h4>;
      case "h5":
        return <h5 className={getitleClasses} style={titleCss}>{title}</h5>;
      case "h6":
        return <h6 className={getitleClasses} style={titleCss}>{title}</h6>;
      case "p":
        return <p className={getitleClasses} style={titleCss}>{title}</p>;
      default:
        return <h3 className={getitleClasses} style={titleCss}>{title}</h3>;
    }
  };
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {Icon && (
        <div
          className={`${iconBgColor} p-1 rounded-lg flex items-center justify-center`}
        >
          {imageSrc && <img src={imageSrc} alt={title} className={`object-contain size-${iconSize}`} />}
          {!imageSrc && Icon && <Icon className={`size-${iconSize} ${iconColor}`} />}
        </div>
      )}
      <div>
        <Title />
        {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
      </div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, subtitle, className = "" }) => {
  return (
    <div className={`text-center py-12 ${className}`}>
      {Icon && (
        <div className="w-16 h-16 bg-customGrayExtraLight rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon className="w-8 h-8 text-customGray" />
        </div>
      )}
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      {subtitle && <p className="text-gray-600">{subtitle}</p>}
    </div>
  );
};

export const SearchInput = ({
  className = "",
  value,
  onChange,
  placeholder,
  t,
  sumOfItems = 0,
  totalItems = 0,
  showCount = false,
}) => {
  return (
    <div className={`${className}`}>
      <div className="relative">
        <Input
          placeholder={placeholder || t("general.search")}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="rtl"
        />
        <div
          dir="auto"
          className="absolute start-2 top-1/2 transform -translate-y-1/2 text-gray-500"
        >
          <FiSearch size={18} />
        </div>
      </div>
      {showCount && (
        <span className="text-sm text-gray-500 mt-1">
          {sumOfItems > 0 && totalItems > 0
            ? t("general.searchResultsOf", {
                count: sumOfItems,
                total: totalItems,
              })
            : sumOfItems > 0
            ? t("general.searchResults", { count: sumOfItems })
            : t("general.noResults")}
        </span>
      )}
    </div>
  );
};
